import type { LucideIcon } from "lucide-react";
import { MapPin, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  addressDetailsLine,
  addressStreetLine,
  type AddressLinesSource,
} from "@/components/shared/address/addressFormat";
import type { ClientAddress, CustomerData, Order } from "@/lib/api";

interface OrderAddressesProps {
  order: Order;
}

/** Endereço legado do cadastro do cliente (client.client_address...), usado quando o pedido não traz o endereço. */
const legacyAddress = (customer?: CustomerData): AddressLinesSource | null =>
  customer?.clientAddress
    ? {
        street: customer.clientAddress,
        number: customer.clientNumber,
        complement: customer.clientComplement,
        district: customer.clientDistrict,
        city: customer.clientCity ?? "",
        state: customer.clientState ?? "",
        postcode: customer.clientPostCode ?? "",
      }
    : null;

interface AddressBlockProps {
  title: string;
  icon: LucideIcon;
  address: ClientAddress | null | undefined;
  fallback: AddressLinesSource | null;
  /** Observação quando o endereço vem do pedido. */
  note?: string;
  /** Observação quando o endereço vem do cadastro legado. */
  fallbackNote: string;
}

function AddressBlock({ title, icon: Icon, address, fallback, note, fallbackNote }: AddressBlockProps) {
  const shown = address ?? fallback;

  return (
    <section className="space-y-2" aria-label={title}>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <h3 className="font-medium">{title}</h3>
      </div>
      {shown ? (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium break-words">{addressStreetLine(shown)}</span>
            {address?.label && <Badge variant="outline">{address.label}</Badge>}
            {address && !address.isActive && <Badge variant="secondary">Desativado</Badge>}
          </div>
          <p className="text-sm text-muted-foreground break-words">{addressDetailsLine(shown)}</p>
          {(address ? note : fallbackNote) && (
            <p className="text-xs text-muted-foreground">{address ? note : fallbackNote}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Endereço não informado.</p>
      )}
    </section>
  );
}

/**
 * Endereços de faturamento e de entrega do pedido (customerGroupAddressFeature). O de entrega é o gravado no pedido;
 * o de faturamento é o padrão de cobrança atual da escola. Pedidos antigos, sem endereço de entrega vinculado
 * (shippingAddress null), usam o de faturamento como entrega. Sem endereço de faturamento, vale o do cadastro.
 */
export function OrderAddresses({ order }: OrderAddressesProps) {
  const fallback = legacyAddress(order.customer);
  const billing = order.billingAddress ?? fallback;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" aria-hidden="true" />Endereços
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        <AddressBlock
          title="Endereço de faturamento"
          icon={Receipt}
          address={order.billingAddress}
          fallback={fallback}
          note="Endereço de cobrança padrão da escola."
          fallbackNote="Endereço do cadastro da escola."
        />
        <AddressBlock
          title="Endereço de entrega"
          icon={MapPin}
          address={order.shippingAddress}
          fallback={billing}
          fallbackNote="Pedido sem endereço de entrega vinculado; usado o endereço de faturamento."
        />
      </CardContent>
    </Card>
  );
}
